// Arma la liquidación del mes para mostrarla y exportarla: por cliente, una
// fila por aplicación que junta su costo Cloud y su costo de proveedor.
// Lo usan el módulo Liquidación, el informe PDF y el Excel.

export const PROVEEDORES = { tbbc: 'TBBC', optimize: 'Optimize', axd: 'AXD' };

// Fracción que va a Mesa → texto y clase de área.
export const areaTexto = (f) => {
  if (f === null || f === undefined) return 'Sin área';
  if (f === 1) return 'Mesa de Servicio';
  if (f === 0) return 'Soporte en Sitio';
  return `Compartida ${Math.round(f * 100)}/${Math.round((1 - f) * 100)}`;
};
export const areaCorta = (f) => {
  if (f === null || f === undefined) return 'Sin área';
  if (f === 1) return 'Mesa';
  if (f === 0) return 'Sitio';
  return `${Math.round(f * 100)}/${Math.round((1 - f) * 100)}`;
};
export const areaClase = (f) =>
  (f === null || f === undefined ? 'sin' : f === 1 ? 'mesa' : f === 0 ? 'sitio' : 'ambas');

// Cómo se repartió una línea de proveedor: completo o ÷N.
const repartoProveedor = (l) => (l.modo === 'unitario' ? 'tarifa completa' : `÷${l.n} clientes`);

/**
 * datos = respuesta de GET /liquidacion. Devuelve:
 *  { periodo, trm, totales, porProveedor, clientes: [{ ..., apps: [fila] }], apps }
 * fila = { key, name, cloud, cloud_usd, proveedor, mesa, sitio, frac, origen, notaProveedor, notaCloud }
 */
export const armarLiquidacion = (datos) => {
  const nombreApp = new Map(datos.apps.map((a) => [a.key, a.name]));
  const trm = datos.corte?.trm || null;

  const clientes = datos.clientes.map((c) => {
    const porApp = new Map();
    c.detalle.forEach((l) => {
      if (!porApp.has(l.app)) {
        porApp.set(l.app, {
          key: l.app, name: nombreApp.get(l.app) || l.concepto,
          cloud: 0, cloud_usd: 0, tieneCloud: false, proveedor: 0, mesa: 0, sitio: 0, sinArea: 0,
          fracs: new Set(), lineasProv: [], lineaCloud: null
        });
      }
      const f = porApp.get(l.app);
      f.fracs.add(l.frac_mesa === null ? 'sin' : l.frac_mesa);
      if (l.tipo === 'cloud') {
        f.tieneCloud = true;
        f.cloud_usd += l.monto_usd;
        f.cloud += l.monto || 0;
        f.lineaCloud = l;
      } else {
        f.proveedor += l.monto;
        f.lineasProv.push(l);
      }
      if (l.monto !== null) {
        if (l.frac_mesa === null) f.sinArea += l.monto;
        else { f.mesa += l.monto * l.frac_mesa; f.sitio += l.monto * (1 - l.frac_mesa); }
      }
    });
    const apps = Array.from(porApp.values()).map((f) => {
      const fracs = Array.from(f.fracs);
      const frac = fracs.length === 1 && fracs[0] !== 'sin' ? fracs[0] : (fracs.includes('sin') ? null : fracs[0]);
      const origen = [...new Set(f.lineasProv.map((l) => PROVEEDORES[l.proveedor]))].join(' + ');
      const notaProveedor = f.lineasProv
        .map((l) => `${PROVEEDORES[l.proveedor]} · ${l.concepto} (${repartoProveedor(l)})`)
        .join('; ');
      const notaCloud = f.lineaCloud ? `Cloud compartido entre ${f.lineaCloud.n} cliente${f.lineaCloud.n === 1 ? '' : 's'}` : '';
      return {
        key: f.key, name: f.name, frac, origen,
        cloud: trm ? f.cloud : null, cloud_usd: f.cloud_usd, tieneCloud: f.tieneCloud,
        proveedor: f.proveedor, total: (trm ? f.cloud : 0) + f.proveedor,
        mesa: f.mesa, sitio: f.sitio, sinArea: f.sinArea,
        notaProveedor, notaCloud
      };
    }).sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
    return { id: c.id, name: c.name, cloud: c.cloud, cloud_usd: c.cloud_usd, proveedor: c.proveedor, total: c.total, mesa: c.mesa, sitio: c.sitio, sinArea: c.sin_area, apps };
  });

  const sum = (arr, f) => arr.reduce((s, x) => s + (f(x) || 0), 0);
  const totales = {
    total: sum(clientes, (c) => c.total),
    cloud: trm ? sum(clientes, (c) => c.cloud) : null,
    cloud_usd: sum(clientes, (c) => c.cloud_usd),
    proveedor: sum(clientes, (c) => c.proveedor),
    mesa: sum(clientes, (c) => c.mesa),
    sitio: sum(clientes, (c) => c.sitio),
    sinArea: sum(clientes, (c) => c.sinArea)
  };
  const porProveedor = {};
  // Cruce concepto × área: cuánto de Cloud y de cada proveedor va a Mesa y a Sitio.
  // Claves: 'cloud', 'tbbc', 'optimize', 'axd' → { mesa, sitio, sinArea, total }
  const porConcepto = {};
  datos.clientes.forEach((c) => c.detalle.forEach((l) => {
    if (l.tipo === 'proveedor') porProveedor[l.proveedor] = (porProveedor[l.proveedor] || 0) + l.monto;
    if (l.monto === null) return;
    const k = l.tipo === 'cloud' ? 'cloud' : l.proveedor;
    const r = porConcepto[k] || (porConcepto[k] = { mesa: 0, sitio: 0, sinArea: 0, total: 0 });
    r.total += l.monto;
    if (l.frac_mesa === null) r.sinArea += l.monto;
    else { r.mesa += l.monto * l.frac_mesa; r.sitio += l.monto * (1 - l.frac_mesa); }
  }));

  return { periodo: datos.periodo, trm, totales, porProveedor, porConcepto, clientes, apps: datos.apps, contratos: datos.contratos, pendientes: datos.pendientes };
};

// Frases del resumen ejecutivo, calculadas de los datos.
export const conclusiones = (liq, fmt) => {
  const { totales: t, clientes, apps } = liq;
  if (!t.total) return [];
  const pct = (v) => `${((v / t.total) * 100).toFixed(1)}%`;
  const frases = [];
  frases.push(`Mesa de Servicio concentra ${pct(t.mesa)} del costo (${fmt(t.mesa)}) y Soporte en Sitio ${pct(t.sitio)} (${fmt(t.sitio)}).`);
  if (t.cloud !== null) {
    frases.push(`El costo del proveedor es ${pct(t.proveedor)} del total (${fmt(t.proveedor)}) y Cloud ${pct(t.cloud)} (${fmt(t.cloud)}).`);
  }
  const top = clientes.slice(0, 3);
  if (top.length) {
    frases.push(`Los ${top.length} clientes de mayor costo (${top.map((c) => c.name).join(', ')}) suman ${pct(top.reduce((s, c) => s + c.total, 0))} del total.`);
  }
  const app = apps[0];
  if (app) frases.push(`La aplicación de mayor costo es ${app.name}: ${fmt(app.total)} (${pct(app.total)}).`);
  return frases;
};
